import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, BadgeCheck, Building2, CheckCircle2, Compass, Eye, FileCheck2, FileSignature, Globe2, HeartHandshake, Layers3, Lightbulb, LockKeyhole, QrCode, ShieldCheck, Sparkles, Target, Users, Workflow, } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
const About = () => {
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [userType, setUserType] = useState(null);
    const navigate = useNavigate();
    const { t, lang } = useLanguage();
    const tr = (key, fallback) => t?.ui?.about?.[key] || t?.[key] || fallback;
    const copy = t?.ui?.about || {};
    useEffect(() => {
        const updateSession = () => {
            const token = localStorage.getItem("token");
            const storedUser = localStorage.getItem("user");
            setIsLoggedIn(Boolean(token));
            if (!storedUser) {
                setUserType(null);
                return;
            }
            try {
                setUserType(JSON.parse(storedUser)?.type || null);
            }
            catch {
                setUserType(null);
            }
        };
        updateSession();
        window.addEventListener("storage", updateSession);
        window.addEventListener("loginStateChanged", updateSession);
        return () => {
            window.removeEventListener("storage", updateSession);
            window.removeEventListener("loginStateChanged", updateSession);
        };
    }, []);
    const principles = [
        {
            icon: ShieldCheck,
            title: copy.integrity,
            text: copy.integrityText,
            tone: "bg-blue-50 text-blue-700 ring-blue-100",
        },
        {
            icon: Lightbulb,
            title: copy.simplicity,
            text: copy.simplicityText,
            tone: "bg-amber-50 text-amber-700 ring-amber-100",
        },
        {
            icon: Layers3,
            title: copy.traceability,
            text: copy.traceabilityText,
            tone: "bg-violet-50 text-violet-700 ring-violet-100",
        },
        {
            icon: Globe2,
            title: copy.accessibility,
            text: copy.accessibilityText,
            tone: "bg-emerald-50 text-emerald-700 ring-emerald-100",
        },
    ];
    const lifecycle = [
        {
            icon: Building2,
            title: copy.lifecycle1,
            text: copy.lifecycle1Text,
        },
        {
            icon: Users,
            title: copy.lifecycle2,
            text: copy.lifecycle2Text,
        },
        {
            icon: FileSignature,
            title: copy.lifecycle3,
            text: copy.lifecycle3Text,
        },
        {
            icon: QrCode,
            title: copy.lifecycle4,
            text: copy.lifecycle4Text,
        },
    ];
    const audiences = [
        {
            icon: Building2,
            title: copy.organizations,
            text: copy.organizationsText,
            accent: "from-blue-600 to-blue-500",
        },
        {
            icon: Workflow,
            title: copy.admins,
            text: copy.adminsText,
            accent: "from-violet-600 to-indigo-500",
        },
        {
            icon: BadgeCheck,
            title: copy.recipients,
            text: copy.recipientsText,
            accent: "from-cyan-600 to-emerald-500",
        },
    ];
    const isAdmin = isLoggedIn && userType === "Admin";
    const primaryCtaTarget = isAdmin
        ? "/dashboard"
        : isLoggedIn
            ? "/participants"
            : "/requests/create";
    const primaryCtaLabel = isAdmin
        ? copy.openDashboard
        : isLoggedIn
            ? t?.participants || copy.recipients
            : copy.requestAccess;
    return (<main className="min-h-screen overflow-hidden bg-slate-50 text-slate-900">
      <section className="relative isolate overflow-hidden bg-slate-950">
        <div className="absolute inset-0 -z-20 bg-[radial-gradient(circle_at_18%_16%,rgba(37,99,235,0.34),transparent_31%),radial-gradient(circle_at_82%_48%,rgba(8,145,178,0.20),transparent_30%),linear-gradient(135deg,#020617_0%,#0f172a_52%,#172554_100%)]"/>
        <div className="absolute inset-0 -z-10 opacity-30 [background-image:linear-gradient(rgba(148,163,184,0.12)_1px,transparent_1px),linear-gradient(90deg,rgba(148,163,184,0.12)_1px,transparent_1px)] [background-size:48px_48px]"/>

        <div className="mx-auto grid min-h-[460px] max-w-7xl items-center gap-12 px-6 pb-16 pt-10 lg:grid-cols-[1.05fr_0.95fr] lg:px-8 lg:pb-20 lg:pt-14">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3.5 py-2 text-sm font-semibold text-blue-100 backdrop-blur">
              <Sparkles className="h-4 w-4 text-cyan-300"/>
              {copy.badge}
            </div>
            <h1 className="mt-7 max-w-3xl text-4xl font-black leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-6xl">
              {t?.aboutTitle || copy.title}
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-8 text-slate-300 sm:text-lg">
              {t?.aboutSubtitle || copy.subtitle}
            </p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link to={primaryCtaTarget} className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-6 py-3.5 text-sm font-bold text-white shadow-lg shadow-blue-950/20 transition hover:bg-blue-500">
                {primaryCtaLabel}
                <ArrowRight className="h-4 w-4"/>
              </Link>
              <Link to="/" className="inline-flex items-center justify-center rounded-xl border border-white/20 bg-white/5 px-6 py-3.5 text-sm font-bold text-white transition hover:bg-white/10">
                {copy.backHome}
              </Link>
            </div>
          </div>

          <div className="relative mx-auto w-full max-w-lg">
            <div className="absolute -inset-10 rounded-full bg-gradient-to-br from-blue-500/25 via-cyan-400/10 to-violet-500/20 blur-3xl"/>
            <div className="absolute -right-5 -top-5 h-24 w-24 rounded-full border border-white/10 bg-white/5 backdrop-blur-xl"/>
            <div className="absolute -bottom-5 -left-5 h-20 w-20 rounded-full border border-cyan-300/10 bg-cyan-400/10 backdrop-blur-xl"/>

            <div className="relative overflow-hidden rounded-[2rem] border border-white/15 bg-slate-900/70 p-4 shadow-2xl shadow-blue-950/40 backdrop-blur-2xl sm:p-6">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(59,130,246,0.22),transparent_33%),radial-gradient(circle_at_bottom_left,rgba(6,182,212,0.15),transparent_30%)]"/>
              <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-cyan-300/70 to-transparent"/>

              <div className="relative">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex min-w-0 items-center gap-3">
                    <div className="relative flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-400 text-white shadow-lg shadow-blue-950/30 ring-1 ring-white/25">
                      <Compass className="h-7 w-7"/>
                      <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-emerald-400 text-slate-950 ring-4 ring-slate-900">
                        <CheckCircle2 className="h-3.5 w-3.5"/>
                      </span>
                    </div>
                    <div className="min-w-0">
                      <p className="text-[11px] font-black uppercase tracking-[0.22em] text-cyan-300">
                        CertiLink
                      </p>
                      <p className="mt-1 truncate text-lg font-black text-white sm:text-xl">
                        {tr("connectedCertification", lang === "fr" ? "Certification connectée" : "Connected certification")}
                      </p>
                    </div>
                  </div>

                  <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-emerald-300/20 bg-emerald-400/10 px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.16em] text-emerald-300">
                    <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-300"/>
                    {tr("liveStatus", lang === "fr" ? "Sécurisé" : "Secure")}
                  </span>
                </div>

                <div className="mt-6 overflow-hidden rounded-[1.5rem] border border-white/10 bg-white shadow-2xl shadow-slate-950/30">
                  <div className="relative overflow-hidden bg-gradient-to-br from-slate-50 via-white to-blue-50 p-5 sm:p-6">
                    <div className="absolute -right-10 -top-10 h-32 w-32 rounded-full border-[20px] border-blue-100/60"/>
                    <div className="absolute -bottom-12 -left-8 h-28 w-28 rounded-full bg-cyan-100/60 blur-2xl"/>

                    <div className="relative flex items-start justify-between gap-4">
                      <div>
                        <p className="text-[10px] font-black uppercase tracking-[0.2em] text-blue-600">
                          {tr("digitalCredential", lang === "fr" ? "Certificat numérique" : "Digital credential")}
                        </p>
                        <h3 className="mt-2 max-w-[250px] text-xl font-black leading-tight text-slate-950 sm:text-2xl">
                          {tr("trustedJourney", lang === "fr" ? "Un parcours certifié, traçable et vérifiable" : "A certified, traceable and verifiable journey")}
                        </h3>
                      </div>
                      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-slate-950 text-cyan-300 shadow-lg">
                        <FileCheck2 className="h-6 w-6"/>
                      </div>
                    </div>
                  </div>

                  <div className="border-t border-slate-100 bg-slate-950 px-4 py-4 sm:px-5">
                    <div className="grid grid-cols-4 gap-2">
                      {lifecycle.map(({ icon: Icon, title }, index) => (
                        <div key={title} className="relative text-center">
                          {index < lifecycle.length - 1 ? (
                            <div className="absolute left-[62%] top-5 h-px w-[76%] bg-gradient-to-r from-blue-400/60 to-cyan-300/20"/>
                          ) : null}
                          <div className="relative mx-auto flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 bg-white/10 text-cyan-300 shadow-sm">
                            <Icon className="h-4 w-4"/>
                          </div>
                          <p className="mt-2 line-clamp-2 text-[9px] font-bold leading-3.5 text-slate-300">
                            {title}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-2 gap-3">
                  <div className="rounded-2xl border border-white/10 bg-white/[0.07] p-4 text-white backdrop-blur">
                    <Target className="h-5 w-5 text-cyan-300"/>
                    <p className="mt-3 text-xs font-black uppercase tracking-[0.12em] text-slate-200">
                      {t?.ourMission || copy.missionLabel}
                    </p>
                    <p className="mt-1.5 line-clamp-2 text-xs leading-5 text-slate-400">
                      {t?.missionText || copy.mission}
                    </p>
                  </div>

                  <div className="rounded-2xl border border-white/10 bg-white/[0.07] p-4 text-white backdrop-blur">
                    <Eye className="h-5 w-5 text-blue-300"/>
                    <p className="mt-3 text-xs font-black uppercase tracking-[0.12em] text-slate-200">
                      {t?.ourVision || copy.visionLabel}
                    </p>
                    <p className="mt-1.5 line-clamp-2 text-xs leading-5 text-slate-400">
                      {t?.visionText || copy.vision}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="relative z-10 mx-auto -mt-10 max-w-7xl px-6 lg:px-8">
        <div className="grid gap-5 rounded-[2rem] border border-slate-200 bg-white p-5 shadow-xl shadow-slate-200/60 sm:grid-cols-2 lg:grid-cols-4">
          {principles.map(({ icon: Icon, title, text, tone }) => (<article key={title} className="rounded-2xl border border-slate-100 bg-slate-50/70 p-5">
              <div className={`inline-flex rounded-xl p-2.5 ring-1 ${tone}`}>
                <Icon className="h-5 w-5"/>
              </div>
              <h2 className="mt-4 font-black text-slate-950">{title}</h2>
              <p className="mt-2 text-sm leading-6 text-slate-600">{text}</p>
            </article>))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-24 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <p className="text-xs font-black uppercase tracking-[0.2em] text-blue-600">
            {copy.lifecycleEyebrow}
          </p>
          <h2 className="mt-4 text-3xl font-black tracking-tight text-slate-950 sm:text-4xl">
            {copy.lifecycleTitle}
          </h2>
          <p className="mt-4 text-base leading-7 text-slate-600">{copy.lifecycleSubtitle}</p>
        </div>

        <div className="mt-14 grid gap-5 lg:grid-cols-4">
          {lifecycle.map(({ icon: Icon, title, text }, index) => (<article key={title} className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <span className="absolute right-4 top-2 text-6xl font-black text-slate-50">
                {String(index + 1).padStart(2, "0")}
              </span>
              <div className="relative inline-flex rounded-xl bg-blue-50 p-3 text-blue-700 ring-1 ring-blue-100">
                <Icon className="h-5 w-5"/>
              </div>
              <h3 className="relative mt-6 font-black text-slate-950">{title}</h3>
              <p className="relative mt-3 text-sm leading-6 text-slate-600">{text}</p>
              {index < lifecycle.length - 1 ? (<ArrowRight className="absolute bottom-5 right-5 hidden h-4 w-4 text-blue-300 lg:block"/>) : null}
            </article>))}
        </div>
      </section>

      <section className="border-y border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-6 py-24 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <p className="text-xs font-black uppercase tracking-[0.2em] text-cyan-700">
              {copy.audienceEyebrow}
            </p>
            <h2 className="mt-4 text-3xl font-black tracking-tight text-slate-950 sm:text-4xl">
              {copy.audienceTitle}
            </h2>
          </div>

          <div className="mt-12 grid gap-6 lg:grid-cols-3">
            {audiences.map(({ icon: Icon, title, text, accent }) => (<article key={title} className="group overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition hover:-translate-y-1 hover:shadow-xl">
                <div className={`h-1.5 bg-gradient-to-r ${accent}`}/>
                <div className="p-6">
                  <div className="inline-flex rounded-xl bg-slate-100 p-3 text-slate-800 transition group-hover:bg-blue-50 group-hover:text-blue-700">
                    <Icon className="h-6 w-6"/>
                  </div>
                  <h3 className="mt-5 text-lg font-black text-slate-950">{title}</h3>
                  <p className="mt-3 text-sm leading-6 text-slate-600">{text}</p>
                </div>
              </article>))}
          </div>
        </div>
      </section>

      <section className="mx-auto grid max-w-7xl items-center gap-12 px-6 py-24 lg:grid-cols-[0.92fr_1.08fr] lg:px-8">
        <div className="relative overflow-hidden rounded-[2rem] bg-slate-950 p-7 shadow-2xl sm:p-9">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(37,99,235,0.35),transparent_35%),radial-gradient(circle_at_bottom_left,rgba(6,182,212,0.18),transparent_30%)]"/>
          <div className="relative">
            <div className="inline-flex rounded-xl bg-white/10 p-3 text-cyan-300 ring-1 ring-white/10">
              <LockKeyhole className="h-6 w-6"/>
            </div>
            <div className="mt-8 space-y-3">
              {[copy.security1, copy.security2, copy.security3, copy.security4].map((item) => (<div key={item} className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/[0.06] p-4">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-300"/>
                    <p className="text-sm font-semibold leading-6 text-slate-200">{item}</p>
                  </div>))}
            </div>
          </div>
        </div>

        <div>
          <p className="text-xs font-black uppercase tracking-[0.2em] text-blue-600">
            {copy.securityEyebrow}
          </p>
          <h2 className="mt-4 text-3xl font-black tracking-tight text-slate-950 sm:text-4xl">
            {copy.securityTitle}
          </h2>
          <p className="mt-5 text-base leading-8 text-slate-600">{copy.securityText}</p>
          <div className="mt-8 flex items-center gap-4 rounded-2xl border border-emerald-100 bg-emerald-50 p-5">
            <div className="rounded-xl bg-white p-3 text-emerald-700 shadow-sm">
              <HeartHandshake className="h-6 w-6"/>
            </div>
            <div>
              <p className="font-black text-slate-950">CertiLink</p>
              <p className="mt-1 text-sm text-slate-600">
                {tr("trustTechnology", "Une technologie au service de la confiance entre organismes et bénéficiaires.")}
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="px-6 pb-24 lg:px-8">
        <div className="relative mx-auto max-w-7xl overflow-hidden rounded-[2rem] bg-gradient-to-br from-blue-700 via-blue-800 to-slate-950 px-6 py-12 text-center shadow-2xl sm:px-10 lg:py-16">
          <div className="absolute -left-20 -top-20 h-56 w-56 rounded-full bg-cyan-400/20 blur-3xl"/>
          <div className="absolute -bottom-24 right-0 h-64 w-64 rounded-full bg-violet-500/20 blur-3xl"/>
          <div className="relative mx-auto max-w-3xl">
            <h2 className="text-3xl font-black tracking-tight text-white sm:text-4xl">
              {copy.ctaTitle}
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-sm leading-6 text-blue-100 sm:text-base">
              {copy.ctaText}
            </p>
            <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
              <button type="button" onClick={() => navigate(primaryCtaTarget)} className="inline-flex items-center justify-center gap-2 rounded-xl bg-white px-6 py-3.5 text-sm font-bold text-blue-800 transition hover:bg-blue-50">
                {primaryCtaLabel}
                <ArrowRight className="h-4 w-4"/>
              </button>
              <Link to="/" className="inline-flex items-center justify-center rounded-xl border border-white/20 bg-white/5 px-6 py-3.5 text-sm font-bold text-white transition hover:bg-white/10">
                {copy.backHome}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>);
};
export default About;